// Q9 S3 — VisitReport wizard step indicator (D9 + D24 + D26).
// Shows the visible step list with current/completed/incomplete state.
// Click on a completed step navigates back (D24 free back-nav). Forward
// nav requires sequential completion. Yellow incomplete-indicator dot
// marks visited-but-incomplete steps (D9 — section can advance with
// "incomplete", but submit is blocked until cleared; the wizard wires
// the submit gate, the indicator surfaces the state).

import type { JSX } from "react";

export type StepStatus = "future" | "current" | "complete" | "incomplete";

export interface StepDescriptor {
  id: string;
  label: string;
}

interface Props {
  steps: readonly StepDescriptor[];
  currentStep: string;
  completedSteps: ReadonlySet<string>;
  incompleteSteps?: ReadonlySet<string>;
  onStepClick: (stepId: string) => void;
}

function classifyStep(
  stepId: string, currentStep: string,
  completedSteps: ReadonlySet<string>, incompleteSteps: ReadonlySet<string>,
): StepStatus {
  if (stepId === currentStep) return "current";
  if (incompleteSteps.has(stepId)) return "incomplete";
  if (completedSteps.has(stepId)) return "complete";
  return "future";
}

const DOT_CLASSES: Record<StepStatus, string> = {
  future:     "bg-slate-200 text-slate-500 border-slate-200",
  current:    "bg-white text-navy-700 border-navy-700 ring-2 ring-navy-200",
  complete:   "bg-navy-700 text-white border-navy-700",
  incomplete: "bg-amber-100 text-amber-800 border-amber-400",
};

const LABEL_CLASSES: Record<StepStatus, string> = {
  future:     "text-slate-400",
  current:    "text-navy-700 font-medium",
  complete:   "text-slate-700",
  incomplete: "text-amber-700",
};

export function WizardStepIndicator({
  steps, currentStep, completedSteps, incompleteSteps = new Set(), onStepClick,
}: Props): JSX.Element {
  return (
    <nav aria-label="Visit report progress" className="mb-5">
      <ol className="flex flex-wrap items-start gap-x-4 gap-y-3 sm:gap-x-6">
        {steps.map((step, idx) => {
          const status = classifyStep(step.id, currentStep, completedSteps, incompleteSteps);
          const clickable = status === "complete" || status === "incomplete" || status === "current";
          const label = step.label;
          return (
            <li key={step.id} className="flex flex-col items-center gap-1 min-w-0">
              <button
                type="button"
                onClick={() => clickable && onStepClick(step.id)}
                disabled={!clickable}
                aria-current={status === "current" ? "step" : undefined}
                aria-label={`Step ${idx + 1}: ${label} (${status})`}
                data-testid={`step-${step.id}`}
                data-status={status}
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-xs font-semibold transition-colors ${DOT_CLASSES[status]} ${clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
              >
                {status === "complete" ? "✓" : status === "incomplete" ? "!" : idx + 1}
              </button>
              <span className={`text-xs ${LABEL_CLASSES[status]}`}>{label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
